import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@material-ui/core';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IEditForm } from '../../../types/interfaces/IEditForm';
import { endpoints } from '../../../utils/endpoints';
import tPaths from '../../../utils/tPaths';
import useApi from '../../../utils/useApi';
import InputForm from '../../../components/InputForm/InputForm';
import useSnackbar from '../../../utils/useSnackbar';

enum EFields {
  NAME = 'name',
  DESCRIPTION = 'description',
}

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const initState = {
  [EFields.NAME]: '',
  [EFields.DESCRIPTION]: '',
};

const AddCruiseLineDialog: React.FC<IProps> = ({ isOpen, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const snackbar = useSnackbar();
  const { post } = useApi(null);
  const [state, setState] = useState(initState);

  useEffect(() => {
    if (isOpen) {
      setState(initState);
    }
  }, [isOpen]);

  const onChange = useCallback((fieldName: string, value: string) => {
    setState((s) => ({ ...s, [fieldName]: value }));
  }, []);

  const fields: Record<EFields, IEditForm> = useMemo(
    () => ({
      [EFields.NAME]: {
        fieldName: EFields.NAME,
        type: 'input',
        label: t(tPaths.editFormLabels.name),
        value: state[EFields.NAME],
        onChange,
        fullWidth: true,
      },
      [EFields.DESCRIPTION]: {
        fieldName: EFields.DESCRIPTION,
        type: 'input',
        label: t(tPaths.editFormLabels.description),
        value: state[EFields.DESCRIPTION],
        onChange,
        fullWidth: true,
        multiline: true,
      },
    }),
    [onChange, state, t],
  );

  const handleSubmit = useCallback(() => {
    if (!state[EFields.NAME].trim()) {
      snackbar({ severity: 'error', message: t(tPaths.global.wentWrong) });
      return;
    }

    const body: Record<string, unknown> = {
      name: { en: state[EFields.NAME].trim() },
      status: 'Active',
    };

    if (state[EFields.DESCRIPTION].trim()) {
      body.description = { en: state[EFields.DESCRIPTION].trim() };
    }

    post({
      endpoint: endpoints.cruiseLines,
      body,
      onSuccess: () => {
        snackbar({ severity: 'success', message: t(tPaths.itemContent.successOnAdd) });
        onSuccess();
        onClose();
      },
    });
  }, [onClose, onSuccess, post, snackbar, state, t]);

  return (
    <Dialog open={isOpen} fullWidth maxWidth="sm" onClose={onClose}>
      <DialogTitle>
        <Typography variant="h6">{t(tPaths.itemContent.addCruiseLine)}</Typography>
      </DialogTitle>
      <DialogContent>
        <Box pt={1}>
          <InputForm {...fields[EFields.NAME]} />
        </Box>
        <Box pt={3}>
          <InputForm {...fields[EFields.DESCRIPTION]} />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t(tPaths.global.cancel)}</Button>
        <Button color="primary" onClick={handleSubmit}>
          {t(tPaths.itemContent.addCruiseLine)}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddCruiseLineDialog;
